/* prestigein.co: ficha de producto. Port del rediseño (prestige-dark/dist), versiones vigentes:
   - app.js: normalize (9), description (19), renderProduct (20, estructura en sections/prestige-product.liquid),
     sizeOption/variantHeading/variantLabel (21-31), setImage (42), clics de galería/variante/riel/zoom (53-54)
   - experience.js: modelKey, sameModelProducts, modelOptionLabel, modelSwatches (33-53)
   Los datos salen de Shopify: <script id="prestige-product-json"> (product | json) y <script id="prestige-product-extra">.
   Las funciones puras se exponen en window.PrestigeProduct para poder compararlas con las del rediseño. */
(() => {
  'use strict';

  /* ---------- Funciones puras (copias literales del rediseño) ---------- */
  const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  function normalize(s) { return String(s).normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase().replace(/[-&]/g, ' ').replace(/\s+/g, ' ').trim(); }
  // Shopify: setup() añade options_with_values ({name, position, values}) desde #prestige-product-extra; catalog.json trae options con esa forma.
  function sizeOption(p) {
    return (p.options_with_values || p.options || []).find((o) => o && typeof o === 'object' && /^(SIZE|TALLA|TAMANO)(\b|$)/i.test(normalize(o.name)));
  }
  function variantHeading(p) { return sizeOption(p) ? 'Selecciona tu talla' : p.category === 'perfumes' ? 'Presentación' : p.variants.length > 1 ? 'Opciones disponibles' : 'Presentación'; }
  function variantLabel(v, p) {
    const size = sizeOption(p);
    if (size) {
      const index = Number(size.position || 1) - 1;
      const value = String(v['option' + (index + 1)] || String(v.title).split(' / ')[index] || '').trim();
      const valid = (size.values || []).includes(value) && /^(?:(?:US|UK|EU|COL)\s*)?(?:\d{1,2}(?:[.,]\d|[½⅓⅔])?|[2-6]?X{0,3}[SML]|[SML]|U|UNICA|ONE SIZE|OS)(?:\s*(?:US|UK|EU|COL))?$/i.test(normalize(value));
      return valid ? 'Talla ' + (/^(U|UNICA|ONE SIZE|OS)$/i.test(normalize(value)) ? 'única' : value) : 'Talla por confirmar';
    }
    if (v.title === 'Default Title' || p.variants.length === 1 && normalize(v.title) === normalize(p.brand) || /^PARFUM|^PERFUME|^GAFAS|^OPTIC|^SOL\b/i.test(v.title)) return p.title.match(/\d+\s*(?:ML|ml|gr|GR)\b/)?.[0] || 'Única';
    return v.title;
  }
  function modelKey(p) {
    const t = normalize(p.title);
    if (p.category === 'gafas') {
      const compact = t.replace(/\s/g, '');
      const model = compact.match(/^0?(?:OW|OO|OX|RBR|RB|RX|RW|VE|AX|AN|AR|EA|DG|MK|HC)\d{4}[A-Z]?/)?.[0] || compact.match(/^0?(?:PR|PS|MU)\d{2}[A-Z]{2}/)?.[0];
      return model ? normalize(p.brand) + ':' + model : 'id:' + p.id;
    }
    if (p.category === 'ropa') {
      const named = t.match(/^(.+?\b(?:MEN|WOMEN|UNISEX|HOMBRE|MUJER))\s+.+$/);
      return named ? normalize(p.brand) + ':' + named[1] : 'id:' + p.id;
    }
    if (p.category === 'perfumes') {
      if (/\b(?:SET|KIT|ESTUCHE|BODY|MIST|CREAM|LOTION|DESODORANTE)\b/.test(t)) return 'id:' + p.id;
      const name = t.replace(/\b\d+(?:[.,]\d+)?\s*(?:ML|OZ|GR)\b/g, '').replace(/\b(?:EAU DE PARFUM|EAU DE TOILETTE|EXTRAIT DE PARFUM|EDP|EDT)\b/g, '').replace(/\s+/g, ' ').trim();
      return normalize(p.brand) + ':' + name;
    }
    return 'id:' + p.id;
  }
  function modelOptionLabel(p) {
    if (p.category === 'gafas') { const pieces = (p.variants[0]?.title || '').split('/'); if (pieces.length > 2) return pieces.slice(1, 3).join(' · '); }
    if (p.category === 'ropa') return p.title.match(/\b(?:MEN|WOMEN|UNISEX|HOMBRE|MUJER)\s+(.+)$/i)?.[1] || p.title;
    return p.title.match(/(?:EDP|EDT|EXTRAIT DE PARFUM|EAU DE PARFUM|EAU DE TOILETTE)?\s*\d+(?:[.,]\d+)?\s*ML\b/i)?.[0]?.trim() || p.title;
  }
  function description(p) {
    const doc = new DOMParser().parseFromString(p.body_html ?? p.description ?? '', 'text/html');
    doc.querySelectorAll('script,style,iframe').forEach((n) => n.remove());
    return doc.body.textContent.trim();
  }
  // image_url ya trae ?width=: se reemplaza el parámetro en vez de añadir otro.
  const photo = (src, w) => { if (!src) return ''; try { const u = new URL(src, location.href); u.searchParams.set('width', w); return u.href; } catch { return src; } };
  const HERO_WIDTHS = [600, 900, 1200, 1600];

  const api = { esc, normalize, sizeOption, variantHeading, variantLabel, modelKey, modelOptionLabel, description, photo };
  (typeof window !== 'undefined' ? window : globalThis).PrestigeProduct = api;
  if (typeof document === 'undefined') return;

  /* ---------- Estado de la ficha ---------- */
  const $ = (s, r = document) => r.querySelector(s);
  const root = () => window.Shopify?.routes?.root || '/';
  const money = (cents) => (window.Prestige?.money ? window.Prestige.money(cents) : new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(Number(cents) / 100));
  const readJSON = (id) => { try { return JSON.parse(document.getElementById(id)?.textContent || 'null'); } catch { return null; } };
  let state = null;

  function setup() {
    const el = $('.pdp[data-prestige-product]');
    if (!el || el.dataset.prestigeReady) return;
    const raw = readJSON('prestige-product-json'), extra = readJSON('prestige-product-extra');
    if (!raw || !extra || !Array.isArray(raw.variants)) return;
    el.dataset.prestigeReady = '1';
    // product | json trae options como lista de nombres; las opciones con valores y posición vienen de #prestige-product-extra.
    const product = { ...raw, brand: extra.brand, category: extra.category, options_with_values: extra.options };
    const input = $('input[name="id"]', el);
    const selected = product.variants.find((v) => String(v.id) === String(input?.value)) || product.variants.find((v) => v.available) || product.variants[0];
    state = { el, product, extra, selected, images: extra.images || [], index: 0, busy: false };
    hydrate();
    loadSiblings();
    initRecs();
  }

  // El HTML de Liquid ya trae etiquetas, encabezado y descripción; aquí se recalculan con las funciones exactas del rediseño.
  function hydrate() {
    const { el, product } = state;
    product.variants.forEach((v) => {
      const b = $('.variant[data-variant="' + v.id + '"]', el);
      const label = variantLabel(v, product);
      if (b && b.textContent !== label) b.textContent = label;
    });
    const hd = $('.variant-heading', el), heading = variantHeading(product);
    if (hd?.firstChild?.nodeType === 3 && hd.firstChild.nodeValue !== heading) hd.firstChild.nodeValue = heading;
    const desc = description(product);
    const short = desc.slice(0, 600) + (desc.length > 600 ? '…' : '');
    const p1 = $('.product-description p', el);
    if (p1 && p1.textContent !== short) p1.textContent = short;
    const details = $('details:first-of-type p:nth-of-type(2)', el);
    if (details && desc && details.textContent !== desc) details.textContent = desc;
  }

  /* ---------- Galería (setImage, app.js:42) ---------- */
  function setImage(i) {
    const { el, images } = state;
    if (!images.length) return;
    state.index = (i + images.length) % images.length;
    const hero = $('#hero-image', el);
    if (hero) {
      hero.src = photo(images[state.index], 1200);
      hero.srcset = HERO_WIDTHS.map((w) => photo(images[state.index], w) + ' ' + w + 'w').join(', ');
    }
    el.querySelectorAll('.thumb').forEach((b, j) => b.classList.toggle('active', j === state.index));
    const count = $('.image-count', el);
    if (count) count.textContent = String(state.index + 1).padStart(2, '0') + ' / ' + String(images.length).padStart(2, '0');
  }
  function zoom() {
    const { images, product } = state, dialog = $('#zoom-dialog'), img = $('#zoom-img');
    if (!images.length || !dialog || !img) return;
    img.src = photo(images[state.index], 1800);
    img.alt = product.title;
    if (!dialog.open) dialog.showModal();
  }

  /* ---------- Variantes (app.js:53) ---------- */
  function selectVariant(id) {
    const { el, product, extra } = state;
    const v = product.variants.find((x) => String(x.id) === String(id));
    if (!v) return;
    state.selected = v;
    el.querySelectorAll('.variant').forEach((x) => {
      const on = x.dataset.variant === String(v.id);
      x.classList.toggle('selected', on);
      x.setAttribute('aria-pressed', String(on));
    });
    const price = $('#product-price', el), small = $('small', price || el);
    if (price) price.innerHTML = money(v.price) + '<small>' + esc(small ? small.textContent : 'COP') + '</small>';
    const add = $('#add-bag', el);
    if (add) {
      add.disabled = !v.available;
      add.textContent = v.available ? (el.dataset.addLabel || 'Agregar a la bolsa') : (el.dataset.soldLabel || 'Agotado');
    }
    const input = $('input[name="id"]', el);
    if (input) input.value = v.id;
    try { const u = new URL(location.href); u.searchParams.set('variant', v.id); history.replaceState(history.state, '', u); } catch { /* sin historial */ }
    const jump = extra.variantImage && extra.variantImage[v.id];
    if (jump !== undefined) setImage(jump);
  }

  /* ---------- Eventos (un solo juego de listeners en document) ---------- */
  document.addEventListener('click', (e) => {
    if (!state) return;
    const b = e.target.closest('button,a');
    const { el } = state;
    if (b && el.contains(b)) {
      if (b.dataset.image !== undefined) setImage(Number(b.dataset.image));
      if (b.dataset.slide) setImage(state.index + Number(b.dataset.slide));
      if (b.dataset.variant) selectVariant(b.dataset.variant);
    }
    if (b?.dataset.rail) {
      const rail = $('.product-rail', b.closest('.recommendations') || document);
      if (rail) rail.scrollBy({ left: Number(b.dataset.rail) * rail.clientWidth * 0.8, behavior: 'smooth' });
    }
    if (e.target.closest('#photo-stage') && !b) zoom();
  });
  document.addEventListener('keydown', (e) => {
    if (state && e.target.id === 'photo-stage' && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); zoom(); }
  });
  // El formulario nativo sigue siendo válido sin JS; con JS se usa la bolsa del rediseño (Ajax Cart API).
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!state || !form.matches?.('form.prestige-product-form') || !window.PrestigeCart) return;
    e.preventDefault();
    const v = state.selected;
    if (!v?.available || state.busy) return;
    state.busy = true;
    Promise.resolve(window.PrestigeCart.add([{ id: Number(v.id), quantity: 1 }])).catch(() => {}).finally(() => { state.busy = false; });
  });

  /* ---------- Colores de este modelo / Presentaciones (modelSwatches, experience.js:53) ---------- */
  const productHref = (handle) => root() + 'products/' + encodeURIComponent(handle);
  function swatch(s, current) {
    const label = modelOptionLabel(s);
    const idx = Math.max(0, s.category === 'gafas' ? s.images.findIndex((i) => /__(?:qt|fr)\./i.test(i.src)) : 0);
    const src = (s.images[idx] || s.images[0] || {}).src;
    const chosen = s.id === current.id;
    const img = src
      ? '<img src="' + esc(photo(src, 160)) + '" srcset="' + esc(photo(src, 160) + ' 160w, ' + photo(src, 320) + ' 320w') + '" sizes="86px" alt="' + esc(s.title) + '" loading="lazy">'
      : '<div class="empty">Imagen no disponible</div>';
    return '<a href="' + esc(productHref(s.handle)) + '" class="' + (chosen ? 'chosen' : '') + '" aria-current="' + (chosen ? 'true' : 'false') + '" title="' + esc(label) + '" aria-label="' + esc(label) + '">' + img + '<span>' + esc(label) + '</span></a>';
  }
  const imageSrc = (x) => x.featured_image?.url || x.image || '';
  async function loadSiblings() {
    const { el, product } = state;
    const key = modelKey(product);
    if (key.startsWith('id:')) return;
    try {
      const q = key.slice(key.indexOf(':') + 1);
      const r = await fetch(root() + 'search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=10', { headers: { Accept: 'application/json' } });
      if (!r.ok) return;
      const found = ((await r.json()).resources?.results?.products || []).filter((x) =>
        x.id !== product.id && imageSrc(x) && modelKey({ id: x.id, title: x.title, brand: product.brand, category: product.category }) === key);
      if (!found.length) return;
      const siblings = await Promise.all(found.map(async (x) => {
        let images = [{ src: imageSrc(x) }], variants = [];
        if (product.category === 'gafas') {
          // El color de las gafas vive en variants[0].title y la foto de catálogo (__qt/__fr) en images: Ajax Product API.
          try {
            const pr = await fetch(productHref(x.handle) + '.js', { headers: { Accept: 'application/json' } });
            if (pr.ok) { const j = await pr.json(); images = (j.images || []).map((src) => ({ src })); variants = j.variants || []; if (!images.length) images = [{ src: imageSrc(x) }]; }
          } catch { /* se queda con la foto destacada */ }
        }
        return { id: x.id, title: x.title, handle: x.handle, category: product.category, brand: product.brand, images, variants };
      }));
      const me = { id: product.id, title: product.title, handle: product.handle, category: product.category, brand: product.brand, variants: product.variants, images: state.images.map((src) => ({ src })) };
      const list = [me, ...siblings];
      const anchor = $('.variant-heading', el);
      if (!anchor || $('.model-swatches', el)) return;
      anchor.insertAdjacentHTML('beforebegin', '<div class="sibling-label">' + (product.category === 'perfumes' ? 'Presentaciones de esta fragancia' : 'Colores de este modelo') + '<span>' + list.length + ' opciones</span></div><div class="swatches model-swatches">' + list.map((s) => swatch(s, me)).join('') + '</div>');
    } catch { /* sin hermanos: la ficha funciona igual */ }
  }

  /* ---------- También te puede gustar (Product Recommendations API) ---------- */
  async function initRecs() {
    const sec = $('[data-prestige-recs]');
    if (!sec || sec.dataset.prestigeReady) return;
    sec.dataset.prestigeReady = '1';
    const rail = $('.product-rail', sec);
    let ok = false;
    try {
      const r = await fetch(sec.dataset.url);
      if (r.ok) {
        const cards = new DOMParser().parseFromString(await r.text(), 'text/html').querySelector('[data-prestige-recs-cards]');
        if (cards && cards.children.length) { rail.innerHTML = cards.innerHTML; ok = true; }
      }
    } catch { /* se prueba el respaldo */ }
    if (!ok && sec.dataset.fallback === 'true') ok = await fallbackRecs(sec, rail);
    if (ok) sec.hidden = false;
  }
  // Respaldo: productos de la primera colección de marca (la tarjeta se genera con PrestigeCatalog.cardHTML).
  async function fallbackRecs(sec, rail) {
    const { extra } = state, col = extra.brandCollection, catalog = window.PrestigeCatalog;
    if (!col?.handle || !catalog?.cardHTML) return false;
    try {
      const r = await fetch(root() + 'collections/' + encodeURIComponent(col.handle) + '/products.json?limit=24', { headers: { Accept: 'application/json' } });
      if (!r.ok) return false;
      const limit = Number(sec.dataset.limit) || 10;
      const list = ((await r.json()).products || []).filter((p) => p.id !== extra.id && p.images?.length).slice(0, limit);
      if (!list.length) return false;
      await catalog.ready?.();
      const groups = [{ h: col.handle, t: col.title, c: extra.category, i: 0 }];
      rail.innerHTML = list.map((p) => catalog.cardHTML(p, { collections: groups })).join('');
      return true;
    } catch { return false; }
  }

  setup();
  // Editor: si solo se recargó otra sección (p. ej. recomendaciones) la ficha sigue viva; si se recargó la ficha, .pdp es nuevo.
  document.addEventListener('shopify:section:load', () => {
    if (state?.el.isConnected) initRecs();
    else { state = null; setup(); }
  });
})();
